import { GiftCardSettings } from './giftCardSettingsService';

interface GenerateGiftCardImageOptions {
  giftCardCode: string;
  design: GiftCardSettings;
  businessLogoUrl?: string | null;
  recipientName?: string;
  width?: number; // Default 600px for email
  height?: number; // Calculated based on aspect ratio
}

/**
 * Generate a base64 image of the gift card using Canvas API directly
 * This is more reliable than html2canvas for React components
 */
export async function generateGiftCardImageCanvas(
  options: GenerateGiftCardImageOptions
): Promise<string> {
  const {
    giftCardCode,
    design,
    businessLogoUrl,
    recipientName,
    width = 600,
    height
  } = options;

  // Calculate height based on aspect ratio (1.586:1)
  const cardHeight = height || Math.round(width / 1.586);
  const scale = 2; // Higher resolution for email
  const canvasWidth = width * scale;
  const canvasHeight = cardHeight * scale;

  // Create canvas
  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  // Helper to scale values
  const s = (value: number) => value * scale;

  // Draw background
  ctx.fillStyle = design.background_color || '#FFFFFF';
  const borderRadius = design.border_radius === 'sharp' ? 0 :
                      design.border_radius === 'pill' ? s(48) : s(16);
  
  // Draw rounded rectangle background
  if (borderRadius > 0) {
    ctx.beginPath();
    // Manual rounded rectangle path
    ctx.moveTo(borderRadius, 0);
    ctx.lineTo(canvasWidth - borderRadius, 0);
    ctx.quadraticCurveTo(canvasWidth, 0, canvasWidth, borderRadius);
    ctx.lineTo(canvasWidth, canvasHeight - borderRadius);
    ctx.quadraticCurveTo(canvasWidth, canvasHeight, canvasWidth - borderRadius, canvasHeight);
    ctx.lineTo(borderRadius, canvasHeight);
    ctx.quadraticCurveTo(0, canvasHeight, 0, canvasHeight - borderRadius);
    ctx.lineTo(0, borderRadius);
    ctx.quadraticCurveTo(0, 0, borderRadius, 0);
    ctx.closePath();
    ctx.fill();
  } else {
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
  }

  // Draw gradient overlay for modern style
  if (design.card_style === 'modern') {
    const gradient = ctx.createLinearGradient(0, 0, canvasWidth, canvasHeight);
    gradient.addColorStop(0, design.primary_color);
    gradient.addColorStop(1, design.secondary_color);
    ctx.fillStyle = gradient;
    ctx.globalAlpha = 0.1;
    if (borderRadius > 0) {
      ctx.beginPath();
      // Manual rounded rectangle path
      ctx.moveTo(borderRadius, 0);
      ctx.lineTo(canvasWidth - borderRadius, 0);
      ctx.quadraticCurveTo(canvasWidth, 0, canvasWidth, borderRadius);
      ctx.lineTo(canvasWidth, canvasHeight - borderRadius);
      ctx.quadraticCurveTo(canvasWidth, canvasHeight, canvasWidth - borderRadius, canvasHeight);
      ctx.lineTo(borderRadius, canvasHeight);
      ctx.quadraticCurveTo(0, canvasHeight, 0, canvasHeight - borderRadius);
      ctx.lineTo(0, borderRadius);
      ctx.quadraticCurveTo(0, 0, borderRadius, 0);
      ctx.closePath();
      ctx.fill();
    } else {
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    }
    ctx.globalAlpha = 1.0;
  }

  // Draw decorative elements
  if (design.show_decorative_elements) {
    if (design.card_style === 'modern') {
      // Large blur circle top-right
      const gradient1 = ctx.createRadialGradient(canvasWidth, 0, 0, canvasWidth, 0, s(256));
      gradient1.addColorStop(0, design.primary_color);
      gradient1.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient1;
      ctx.globalAlpha = 0.2;
      ctx.beginPath();
      ctx.arc(canvasWidth, 0, s(256), 0, Math.PI * 2);
      ctx.fill();
      
      // Small blur circle bottom-left
      const gradient2 = ctx.createRadialGradient(0, canvasHeight, 0, 0, canvasHeight, s(192));
      gradient2.addColorStop(0, design.accent_color);
      gradient2.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient2;
      ctx.beginPath();
      ctx.arc(0, canvasHeight, s(192), 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1.0;
    }
  }

  // Load and draw logo if needed
  if (design.include_logo && businessLogoUrl) {
    try {
      const logoImg = await loadImage(businessLogoUrl);
      const logoSize = design.logo_size === 'small' ? s(32) : s(48);
      const maxHeight = s(design.logo_max_height || 48);
      const logoHeight = Math.min(logoSize, maxHeight);
      const logoWidth = (logoImg.width / logoImg.height) * logoHeight;
      
      // Calculate logo position
      const padding = s(32);
      let x = 0, y = 0;
      
      if (design.logo_position.startsWith('top-')) {
        y = padding;
      } else if (design.logo_position.startsWith('center-')) {
        y = canvasHeight / 2 - logoHeight / 2;
      } else if (design.logo_position.startsWith('bottom-')) {
        y = canvasHeight - logoHeight - padding;
      }
      
      if (design.logo_position.endsWith('-left')) {
        x = padding;
      } else if (design.logo_position.endsWith('-center') || design.logo_position === 'center') {
        x = canvasWidth / 2 - logoWidth / 2;
      } else if (design.logo_position.endsWith('-right')) {
        x = canvasWidth - logoWidth - padding;
      }
      
      ctx.drawImage(logoImg, x, y, logoWidth, logoHeight);
    } catch (error) {
      console.warn('Failed to load logo:', error);
    }
  }

  // Draw gift icon if no logo
  if (!design.include_logo || !businessLogoUrl) {
    const iconSize = s(32);
    const iconPadding = s(12);
    const iconX = canvasWidth / 2 - (iconSize + iconPadding * 2) / 2;
    const iconY = s(32);
    
    // Draw icon background circle
    ctx.fillStyle = design.primary_color;
    ctx.beginPath();
    ctx.arc(iconX + iconSize / 2 + iconPadding, iconY + iconSize / 2 + iconPadding, (iconSize + iconPadding * 2) / 2, 0, Math.PI * 2);
    ctx.fill();
    
    // Draw gift icon (simplified - just a rectangle with a bow)
    ctx.fillStyle = '#FFFFFF';
    // Gift box
    ctx.fillRect(iconX + iconPadding + iconSize * 0.2, iconY + iconPadding + iconSize * 0.3, iconSize * 0.6, iconSize * 0.5);
    // Bow
    ctx.beginPath();
    ctx.arc(iconX + iconPadding + iconSize * 0.3, iconY + iconPadding + iconSize * 0.3, iconSize * 0.15, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(iconX + iconPadding + iconSize * 0.7, iconY + iconPadding + iconSize * 0.3, iconSize * 0.15, 0, Math.PI * 2);
    ctx.fill();
  }

  // Set up text styles
  const getFontFamily = () => {
    switch (design.font_family) {
      case 'inter': return 'Inter, sans-serif';
      case 'roboto': return 'Roboto, sans-serif';
      case 'playfair': return '"Playfair Display", serif';
      case 'poppins': return 'Poppins, sans-serif';
      case 'montserrat': return 'Montserrat, sans-serif';
      default: return 'sans-serif';
    }
  };

  // Determine text color based on background
  const isLightBackground = (color: string) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 155;
  };

  const textColor = isLightBackground(design.background_color) 
    ? design.text_color 
    : '#FFFFFF';

  // Draw card title
  ctx.fillStyle = textColor;
  ctx.font = `bold ${s(32)}px ${getFontFamily()}`;
  ctx.textAlign = design.card_title_position === 'left' ? 'left' :
                  design.card_title_position === 'right' ? 'right' : 'center';
  ctx.textBaseline = 'top';
  
  const titleX = design.card_title_position === 'left' ? s(32) :
                 design.card_title_position === 'right' ? canvasWidth - s(32) :
                 canvasWidth / 2;
  const titleY = s(32);
  
  ctx.fillText(design.card_title, titleX, titleY);

  // Draw greeting message
  ctx.fillStyle = textColor;
  ctx.globalAlpha = 0.8;
  ctx.font = `${s(14)}px ${getFontFamily()}`;
  ctx.textAlign = design.greeting_message_position === 'left' ? 'left' :
                  design.greeting_message_position === 'right' ? 'right' : 'center';
  
  const greetingX = design.greeting_message_position === 'left' ? s(32) :
                   design.greeting_message_position === 'right' ? canvasWidth - s(32) :
                   canvasWidth / 2;
  const greetingY = titleY + s(40);
  
  ctx.fillText(design.greeting_message, greetingX, greetingY);
  ctx.globalAlpha = 1.0;

  // Draw gift card code at bottom center
  if (giftCardCode) {
    ctx.fillStyle = textColor;
    ctx.font = `600 ${s(14)}px "Courier New", monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    
    const codeY = canvasHeight - s(16);
    ctx.fillText(giftCardCode, canvasWidth / 2, codeY);
  }

  // Draw accent bar for classic style
  if (design.card_style === 'classic') {
    ctx.fillStyle = design.accent_color;
    ctx.fillRect(0, canvasHeight - s(8), canvasWidth, s(8));
  }

  // Convert to base64
  const imageDataUrl = canvas.toDataURL('image/png', 1.0);
  
  console.log('✅ Canvas image generated successfully:', {
    dataUrlLength: imageDataUrl.length,
    width: canvasWidth,
    height: canvasHeight
  });
  
  return imageDataUrl;
}

/**
 * Helper to load an image with CORS support
 */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (error) => reject(error);
    img.src = src;
    
    // Timeout after 5 seconds
    setTimeout(() => {
      reject(new Error('Image load timeout'));
    }, 5000);
  });
}

